import React, { useState, useEffect } from "react";
import {
  Clock,
  User,
  Download,
  Search,
  Shield,
  AlertCircle,
  CheckCircle,
  Edit,
  Trash2,
  Plus,
  FileText,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Database,
  History,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadAuditLogs = async (entrepriseId, limit = 100) => {
  const { data, error } = await supabase
    .from("audit_logs")
    .select("*")
    .eq("entreprise_id", entrepriseId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    // Si la table n'existe pas, retourner un tableau vide
    if (error.code === "42P01") {
      console.log("Table audit_logs n'existe pas encore.");
      return [];
    }
    throw error;
  }
  return data || [];
};

// ============================================
// COMPOSANTS UI
// ============================================

const LoadingSpinner = ({ message = "Chargement..." }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <Loader2 size={40} className="text-sky-600 animate-spin mb-4" />
    <p className="text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

const EmptyState = ({ tableExists }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-12 text-center">
    <History
      size={64}
      className="mx-auto text-gray-300 dark:text-gray-600 mb-4"
    />
    <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">
      {tableExists
        ? "Aucune activité enregistrée"
        : "Journal d'audit non configuré"}
    </h3>
    <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
      {tableExists
        ? "Les actions effectuées dans l'application apparaîtront ici automatiquement."
        : "Le journal d'audit sera disponible prochainement. Il enregistrera toutes les actions importantes pour la conformité."}
    </p>
    <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4 max-w-md mx-auto">
      <p className="text-sm text-sky-700 dark:text-sky-400">
        📋 Le journal d'audit trace : créations, modifications et suppressions
        de données, incidents, consentements, et autres actions de conformité.
      </p>
    </div>
  </div>
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const AuditTimeline = ({ entrepriseId, showToast }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableExists, setTableExists] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterUser, setFilterUser] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterDate, setFilterDate] = useState("all");

  const actionIcons = {
    create: {
      icon: Plus,
      color: "text-green-600",
      bg: "bg-green-100 dark:bg-green-900/30",
    },
    update: {
      icon: Edit,
      color: "text-blue-600",
      bg: "bg-blue-100 dark:bg-blue-900/30",
    },
    delete: {
      icon: Trash2,
      color: "text-red-600",
      bg: "bg-red-100 dark:bg-red-900/30",
    },
    view: {
      icon: FileText,
      color: "text-purple-600",
      bg: "bg-purple-100 dark:bg-purple-900/30",
    },
    login: {
      icon: User,
      color: "text-sky-600",
      bg: "bg-sky-100 dark:bg-sky-900/30",
    },
  };

  const typeLabels = {
    inventaire: "Inventaire",
    document: "Document",
    incident: "Incident",
    efvp: "EFVP",
    consentement: "Consentement",
    utilisateur: "Utilisateur",
    rapport: "Rapport",
    auth: "Authentification",
  };

  const typeIcons = {
    inventaire: Database,
    document: FileText,
    incident: AlertCircle,
    efvp: Shield,
    consentement: CheckCircle,
    utilisateur: User,
    rapport: FileText,
    auth: User,
  };

  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await loadAuditLogs(entrepriseId);
      setAuditLogs(data);
      setTableExists(true);
    } catch (err) {
      console.error("Erreur:", err);
      if (err.code === "42P01") {
        setTableExists(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadData();
    if (showToast) showToast("Journal actualisé!", "success");
  };

  // Filtrage
  const filteredLogs = auditLogs.filter((log) => {
    const matchesSearch =
      (log.description || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (log.user_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.user_email || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesUser = filterUser === "all" || log.user_email === filterUser;
    const matchesAction = filterAction === "all" || log.action === filterAction;

    let matchesDate = true;
    if (filterDate !== "all" && log.created_at) {
      const logDate = new Date(log.created_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (filterDate === "today") {
        matchesDate = logDate >= today;
      } else if (filterDate === "7days") {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        matchesDate = logDate >= sevenDaysAgo;
      } else if (filterDate === "30days") {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        matchesDate = logDate >= thirtyDaysAgo;
      }
    }

    return matchesSearch && matchesUser && matchesAction && matchesDate;
  });

  const uniqueUsers = [
    ...new Set(auditLogs.map((log) => log.user_email).filter(Boolean)),
  ];

  // Stats
  const stats = {
    create: auditLogs.filter((l) => l.action === "create").length,
    update: auditLogs.filter((l) => l.action === "update").length,
    delete: auditLogs.filter((l) => l.action === "delete").length,
    users: uniqueUsers.length,
  };

  // Export CSV
  const exportToCSV = () => {
    if (filteredLogs.length === 0) return;

    const csv = [
      ["Date/Heure", "Utilisateur", "Email", "Action", "Type", "Description"],
      ...filteredLogs.map((log) => [
        log.created_at,
        log.user_name || "-",
        log.user_email || "-",
        log.action,
        typeLabels[log.type] || log.type,
        log.description,
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `journal_audit_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();

    if (showToast) showToast("Journal exporté!", "success");
  };

  // Grouper par date
  const groupLogsByDate = () => {
    const groups = {};
    filteredLogs.forEach((log) => {
      const date = log.created_at ? log.created_at.split("T")[0] : "unknown";
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(log);
    });
    return groups;
  };

  const groupedLogs = groupLogsByDate();

  // États
  if (!entrepriseId) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
        <p className="text-amber-700 dark:text-amber-300">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading)
    return <LoadingSpinner message="Chargement du journal d'audit..." />;

  if (!tableExists || auditLogs.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
              Journal d'audit
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Historique des actions de conformité
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        <EmptyState tableExists={tableExists} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            Journal d'audit
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {filteredLogs.length} action{filteredLogs.length > 1 ? "s" : ""}{" "}
            enregistrée{filteredLogs.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={exportToCSV}
            disabled={filteredLogs.length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
          >
            <Download size={20} />
            <span>Exporter</span>
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-400">
                Créations
              </p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-300">
                {stats.create}
              </p>
            </div>
            <Plus className="text-green-600 dark:text-green-400" size={32} />
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-400">
                Modifications
              </p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                {stats.update}
              </p>
            </div>
            <Edit className="text-blue-600 dark:text-blue-400" size={32} />
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 dark:text-red-400">
                Suppressions
              </p>
              <p className="text-2xl font-bold text-red-900 dark:text-red-300">
                {stats.delete}
              </p>
            </div>
            <Trash2 className="text-red-600 dark:text-red-400" size={32} />
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-700 dark:text-purple-400">
                Utilisateurs
              </p>
              <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">
                {stats.users}
              </p>
            </div>
            <User className="text-purple-600 dark:text-purple-400" size={32} />
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
          >
            <option value="all">Tous les utilisateurs</option>
            {uniqueUsers.map((email) => (
              <option key={email} value={email}>
                {auditLogs.find((l) => l.user_email === email)?.user_name ||
                  email}
              </option>
            ))}
          </select>

          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
          >
            <option value="all">Toutes les actions</option>
            <option value="create">Création</option>
            <option value="update">Modification</option>
            <option value="delete">Suppression</option>
          </select>

          <select
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
          >
            <option value="all">Toutes les dates</option>
            <option value="today">Aujourd'hui</option>
            <option value="7days">7 derniers jours</option>
            <option value="30days">30 derniers jours</option>
          </select>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        {Object.entries(groupedLogs)
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([date, logs]) => (
            <div key={date} className="mb-8 last:mb-0">
              <div className="flex items-center mb-4">
                <Clock className="text-gray-400 mr-2" size={20} />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {date !== "unknown"
                    ? new Date(date).toLocaleDateString("fr-CA", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "Date inconnue"}
                </h3>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700 ml-4"></div>
              </div>

              <div className="space-y-4 pl-8 border-l-2 border-gray-200 dark:border-gray-700">
                {logs.map((log) => {
                  const actionConfig =
                    actionIcons[log.action] || actionIcons.view;
                  const ActionIcon = actionConfig.icon;
                  const TypeIcon = typeIcons[log.type] || FileText;

                  return (
                    <div key={log.id} className="relative pl-8 pb-4 last:pb-0">
                      {/* Point sur la timeline */}
                      <div
                        className={`absolute left-0 -ml-2 w-4 h-4 rounded-full ${actionConfig.bg} border-2 border-white dark:border-gray-800`}
                      ></div>

                      {/* Carte d'action */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start space-x-3">
                          <div className={`${actionConfig.bg} p-2 rounded-lg`}>
                            <ActionIcon
                              className={actionConfig.color}
                              size={20}
                            />
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                              <p className="font-medium text-gray-900 dark:text-white">
                                {log.description || "Action"}
                              </p>
                              <span className="px-2 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-400 rounded text-xs">
                                {typeLabels[log.type] || log.type}
                              </span>
                            </div>

                            <div className="flex items-center flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                              <span className="flex items-center">
                                <User size={14} className="mr-1" />
                                {log.user_name ||
                                  log.user_email ||
                                  "Utilisateur"}
                              </span>
                              <span className="flex items-center">
                                <Clock size={14} className="mr-1" />
                                {log.created_at
                                  ? new Date(log.created_at).toLocaleTimeString(
                                      "fr-CA",
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )
                                  : "-"}
                              </span>
                            </div>

                            {/* Détails */}
                            {log.details &&
                              Object.keys(log.details).length > 0 && (
                                <div className="bg-white dark:bg-gray-800 rounded p-3 text-xs border border-gray-200 dark:border-gray-600 mt-2">
                                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Détails :
                                  </p>
                                  {Object.entries(log.details).map(
                                    ([key, value]) => (
                                      <div
                                        key={key}
                                        className="text-gray-600 dark:text-gray-400"
                                      >
                                        <strong>{key}:</strong> {String(value)}
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

        {filteredLogs.length === 0 && (
          <div className="text-center py-12">
            <Clock className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-600 dark:text-gray-400">
              Aucune action trouvée avec ces filtres
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditTimeline;
